import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Re-syncs app-discounted prices whenever cart rows are (re)rendered —
   * hydration, section morphs, or third-party re-renders restore the
   * undiscounted server-side prices.
   * @param {MutationRecord[]} mutations
   */
  #handleAppPriceMutations = (mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (
          node.matches?.('.cart-items__table-row, .wsaio_cart_total') ||
          node.querySelector?.('.cart-items__table-row, .wsaio_cart_total')
        ) {
          this.#debouncedAppPriceSync();
          return;
        }
      }
    }
  };

  #appPriceObserver = new MutationObserver(this.#handleAppPriceMutations);
  #debouncedAppPriceSync = debounce(this.#syncAppPrices, 400).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#appPriceObserver.observe(this, { childList: true, subtree: true });
    this.#syncAppPrices();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#appPriceObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#applyAppPrices(parsedResponseText.items);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * Intentionally a no-op: we do not reset the quantity input or show
   * availability errors, so customers can type values above Shopify's cap.
   */
  #handleCartError = () => {
    // Bypass: matches the behavior of the beyond--scqb-upgrade theme.
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#syncAppPrices();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      this.#syncAppPrices(event.detail.data.items);
      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#syncAppPrices();
    }
  };

  /**
   * Fetches /cart.js (when items aren't already in hand) and patches prices.
   * Wholesale/quantity-break apps rewrite cart JSON client-side but the
   * section HTML stays undiscounted; this syncs the DOM with the JSON.
   * No-op when every item's final_price equals its original_price.
   * @param {Array} [items] - Cart items, if already available.
   */
  #syncAppPrices(items) {
    if (items) {
      this.#applyAppPrices(items);
      return;
    }

    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => this.#applyAppPrices(cart.items))
      .catch(() => {});
  }

  /**
   * Queues a price patch on the next frame, after section morphs settle.
   * @param {Array} items - Cart items from /cart.js or cart/change.js.
   */
  #applyAppPrices(items) {
    requestAnimationFrame(() => this.#patchAppPrices(items));
  }

  /**
   * Patches each row's unit price from two sources, whichever shows a
   * discount:
   *  1. The cart JSON (`final_price`, `final_line_price` — some apps rewrite
   *     these client-side while the section HTML stays undiscounted).
   *  2. The app's own injected `.wsaio_cart_total` (line total ÷ quantity).
   * Writes are value-keyed in `data-app-unit` so re-renders re-patch only
   * when the computed price actually changed, and never loop the observer.
   * @param {Array} [items] - Cart items from /cart.js or cart/change.js.
   */
  #patchAppPrices(items) {
    const rows = Array.from(
      this.querySelectorAll('.cart-items__table-row[data-variant-id]')
    );

    for (const row of rows) {
      if (row.classList.contains('removing')) continue;

      const unitPrice = row.querySelector('[data-cart-item-unit-price]');
      if (!unitPrice) continue;

      const quantity = parseInt(row.querySelector('input[data-cart-quantity]')?.value || '', 10);
      if (!quantity) continue;

      const item = Array.isArray(items)
        ? items.find((i) => i.key === row.dataset.key) ||
          items.find((i) => String(i.variant_id) === row.dataset.variantId)
        : null;

      // Source 1: cart JSON fields (lowest available wins).
      let jsonUnit = null;
      let jsonOriginal = null;
      if (item) {
        const candidates = [item.final_price, item.price].filter((v) => typeof v === 'number');
        const lineCandidates = [item.final_line_price, item.line_price].filter(
          (v) => typeof v === 'number'
        );
        const line = lineCandidates.length ? Math.min(...lineCandidates) : null;
        jsonUnit = candidates.length ? Math.min(...candidates) : null;
        if (line != null && (jsonUnit == null || line / quantity < jsonUnit)) {
          jsonUnit = line / quantity;
        }
        jsonOriginal = item.original_price ?? item.final_price ?? item.price ?? null;
      }

      // Source 2: the app's own injected line total in this row.
      const appTotal = row.querySelector('.wsaio_cart_total .money, .wsaio_cart_total .price');
      const appOriginal = row.querySelector('.wsaio_cart_total s');
      const appLineCents = this.#parseMoneyText(appTotal?.textContent);
      const appOriginalCents = this.#parseMoneyText(appOriginal?.textContent);
      const domUnit = appLineCents ? appLineCents / quantity : null;
      const domOriginal = appOriginalCents ? appOriginalCents / quantity : null;

      // Use the lowest discounted unit price available, with its original.
      let unit = null;
      let original = null;
      if (jsonUnit != null && jsonOriginal != null && jsonUnit < jsonOriginal) {
        unit = jsonUnit;
        original = jsonOriginal;
      }
      if (domUnit != null && domOriginal != null && domUnit < domOriginal && (unit == null || domUnit < unit)) {
        unit = domUnit;
        original = domOriginal;
      }

      if (unit == null || original == null) continue;

      const key = `${Math.round(unit)}:${Math.round(original)}`;
      const rendered = this.#parseMoneyText(unitPrice.textContent);
      if (unitPrice.dataset.appUnit === key && rendered === Math.round(unit)) continue;

      unitPrice.dataset.appUnit = key;
      unitPrice.innerHTML =
        `<span class="visually-hidden">${unitPrice.dataset.labelSale || 'Sale price'}</span>` +
        `<span>${this.#formatCents(unit)}</span>` +
        `<span class="visually-hidden">${unitPrice.dataset.labelRegular || 'Regular price'}</span>` +
        `<s class="compare-at-price">${this.#formatCents(original)}</s>`;
    }
  }

  /**
   * Parses a rendered money string like "$2,716.00" into cents.
   * @param {string | null | undefined} text
   * @returns {number | null} Cents, or null when unparseable.
   */
  #parseMoneyText(text) {
    if (!text) return null;
    const match = text.replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    return match ? Math.round(parseFloat(match[1]) * 100) : null;
  }

  /**
   * Formats a cents amount like the storefront money filter.
   * @param {number} cents
   * @returns {string}
   */
  #formatCents(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
